import React from 'react';
import type { CalendarDay as CalendarDayType } from '@abs/shared-types';

export interface CalendarDayCellProps {
  day: CalendarDayType;
}

function getStatusColor(day: CalendarDayType): string | null {
  if (!day.sessionType && !day.absenceEntry) return null;
  if (day.override?.type === 'holiday' || day.override?.type === 'cancelled') return 'var(--color-absence-cancelled)';
  if (!day.absenceEntry) return null;

  const colorMap: Record<string, string> = {
    unexcused: 'var(--color-absence-unexcused)',
    excused: 'var(--color-absence-excused)',
    late: 'var(--color-absence-late)',
    early_departure: 'var(--color-absence-early)',
    present: 'var(--color-absence-present)',
  };

  return colorMap[day.absenceEntry.type] ?? null;
}

function getStatusLabel(day: CalendarDayType): string {
  if (day.override?.type === 'holiday') return 'Holiday';
  if (day.override?.type === 'cancelled') return 'Cancelled';
  if (!day.sessionType) return 'No session';
  if (day.absenceEntry) return day.absenceEntry.type.replace(/_/g, ' ');
  return 'No entry';
}

export function CalendarDayCell({ day }: CalendarDayCellProps): React.ReactElement {
  const color = getStatusColor(day);
  const statusLabel = getStatusLabel(day);
  const dayNum = parseInt(day.date.split('-')[2], 10);

  const ariaLabel = `${new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} — ${statusLabel}`;

  return (
    <div
      className={`calendar-day${day.isToday ? ' calendar-day--today' : ''}${!day.isCurrentMonth ? ' calendar-day--other' : ''}`}
      role="gridcell"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <span className="calendar-day__number">{dayNum}</span>
      {color && (
        <span
          className="calendar-day__dot"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
